import { useState } from 'react'
import { useAdminEmployees, useUserFeatures, useGrantFeatures, useRevokeFeature } from '@/hooks/useAdminUsers'
import { MODULE_KEYS } from '@/hooks/useModuleAccess'

// Split module keys into admin and tenant groups
const ADMIN_MODULES = Object.entries(MODULE_KEYS).filter(([href]) => href.startsWith('/employee/'))
const TENANT_MODULES = Object.entries(MODULE_KEYS).filter(([href]) => href.startsWith('/tenant/'))

// Friendly names for module keys (derived from sidebar nav)
const MODULE_LABELS: Record<string, string> = {
  module_dashboard: 'Dashboard',
  module_tenants: 'Tenants',
  module_features: 'Features',
  module_emails: 'Email Templates',
  module_emp_attestations: 'Attestations',
  module_profile_browser: 'Profile Browser',
  module_mindmetal_admin: 'Personic AI Admin',
  module_devtools: 'Dev Tools',
  module_health: 'System Health',
  module_system: 'System Config',
  module_support: 'Support',
  module_tenant_dashboard: 'Dashboard',
  module_apikeys: 'API Keys',
  module_developers: 'Developers',
  module_profiles: 'Profiles',
  module_engage: 'Engage',
  module_personas: 'Personas',
  module_distillation: 'Distillation',
  module_team: 'Team',
  module_attestations: 'Attestations',
  module_calibration: 'Calibration',
  module_billing: 'Billing',
  module_settings: 'Settings',
}

export default function ModuleAccessManager() {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const { data: employeeData, isLoading: loadingEmployees } = useAdminEmployees({
    search: search || undefined,
  })
  const { data: userFeatures, isLoading: loadingFeatures } = useUserFeatures(selectedUserId)

  const grantMutation = useGrantFeatures()
  const revokeMutation = useRevokeFeature()

  const selectedEmployee = employeeData?.users.find(u => u.id === selectedUserId)

  // Get current module overrides for the selected user
  const currentModuleKeys = new Set(
    (userFeatures || [])
      .filter(f => f.category === 'module' && f.has_access && f.source === 'override')
      .map(f => f.key)
  )

  // Check if user is superuser (all modules via role)
  const isSelectedSuperuser = selectedEmployee?.role === 'superuser'

  const handleToggleModule = async (moduleKey: string, currentlyEnabled: boolean) => {
    if (!selectedUserId) return
    try {
      if (currentlyEnabled) {
        await revokeMutation.mutateAsync({ userId: selectedUserId, featureKey: moduleKey })
      } else {
        await grantMutation.mutateAsync({ userId: selectedUserId, features: [moduleKey] })
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || `Failed to update module access`)
    }
  }

  const handleGrantAll = async (modules: [string, string][]) => {
    if (!selectedUserId) return
    const keysToGrant = modules.map(([, key]) => key).filter(key => !currentModuleKeys.has(key))
    if (keysToGrant.length === 0) return
    try {
      await grantMutation.mutateAsync({ userId: selectedUserId, features: keysToGrant })
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to grant modules')
    }
  }

  const handleRevokeAll = async (modules: [string, string][]) => {
    if (!selectedUserId) return
    const keysToRevoke = modules.map(([, key]) => key).filter(key => currentModuleKeys.has(key))
    for (const key of keysToRevoke) {
      try {
        await revokeMutation.mutateAsync({ userId: selectedUserId, featureKey: key })
      } catch (err: any) {
        // continue revoking others
      }
    }
  }

  const isMutating = grantMutation.isPending || revokeMutation.isPending

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Module Access</h2>
        <p className="text-sm text-gray-500 mt-1">
          Control which sidebar modules each employee can access. Superusers automatically have all modules.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Employee selector */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search employees..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-personic-500 focus:border-transparent"
              />
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              {loadingEmployees && (
                <p className="p-4 text-gray-500 text-center text-sm">Loading...</p>
              )}
              {employeeData?.users.map(user => (
                <button
                  key={user.id}
                  onClick={() => setSelectedUserId(user.id)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 transition-colors ${
                    selectedUserId === user.id
                      ? 'bg-personic-50 border-l-2 border-l-personic-600'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-personic-100 flex items-center justify-center text-personic-600 font-medium text-sm">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">{user.name}</div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </div>
                    {user.role === 'superuser' && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                        Super
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-400">
                    {user.feature_overrides.filter(f => f.startsWith('module_')).length} modules
                  </div>
                </button>
              ))}
              {!loadingEmployees && employeeData?.users.length === 0 && (
                <p className="p-4 text-gray-500 text-center text-sm">No employees found</p>
              )}
            </div>
          </div>
        </div>

        {/* Module checkboxes */}
        <div className="lg:col-span-2">
          {!selectedUserId ? (
            <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
              <p className="text-gray-500">Select an employee to manage their module access</p>
            </div>
          ) : loadingFeatures ? (
            <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
              <p className="text-gray-500">Loading features...</p>
            </div>
          ) : isSelectedSuperuser ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 mb-3">
                  <span className="text-lg">*</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{selectedEmployee?.name}</h3>
                <p className="text-sm text-purple-600 font-medium mt-1">Superuser</p>
                <p className="text-sm text-gray-500 mt-2">
                  Superusers automatically have access to all modules. No configuration needed.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Employee header */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedEmployee?.name}</h3>
                  <p className="text-sm text-gray-500">{selectedEmployee?.email}</p>
                </div>
                <span className="text-sm text-gray-400">
                  {currentModuleKeys.size} modules granted
                </span>
              </div>

              {/* Admin Modules */}
              <ModuleGroup
                title="Admin Modules"
                modules={ADMIN_MODULES}
                currentKeys={currentModuleKeys}
                isMutating={isMutating}
                onToggle={handleToggleModule}
                onGrantAll={() => handleGrantAll(ADMIN_MODULES)}
                onRevokeAll={() => handleRevokeAll(ADMIN_MODULES)}
              />

              {/* Tenant Modules */}
              <ModuleGroup
                title="Tenant Modules"
                modules={TENANT_MODULES}
                currentKeys={currentModuleKeys}
                isMutating={isMutating}
                onToggle={handleToggleModule}
                onGrantAll={() => handleGrantAll(TENANT_MODULES)}
                onRevokeAll={() => handleRevokeAll(TENANT_MODULES)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ModuleGroup({
  title,
  modules,
  currentKeys,
  isMutating,
  onToggle,
  onGrantAll,
  onRevokeAll,
}: {
  title: string
  modules: [string, string][]
  currentKeys: Set<string>
  isMutating: boolean
  onToggle: (key: string, enabled: boolean) => void
  onGrantAll: () => void
  onRevokeAll: () => void
}) {
  const enabledCount = modules.filter(([, key]) => currentKeys.has(key)).length

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
          <p className="text-xs text-gray-500">{enabledCount} of {modules.length} enabled</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onGrantAll}
            disabled={isMutating || enabledCount === modules.length}
            className="px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100 disabled:opacity-50 transition-colors"
          >
            Grant All
          </button>
          <button
            onClick={onRevokeAll}
            disabled={isMutating || enabledCount === 0}
            className="px-2.5 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 disabled:opacity-50 transition-colors"
          >
            Revoke All
          </button>
        </div>
      </div>
      <div className="divide-y divide-gray-100">
        {modules.map(([href, key]) => {
          const isEnabled = currentKeys.has(key)
          return (
            <label
              key={key}
              className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={() => onToggle(key, isEnabled)}
                disabled={isMutating}
                className="h-4 w-4 rounded border-gray-300 text-personic-600 focus:ring-personic-500 disabled:opacity-50"
              />
              <div className="ml-3 flex-1">
                <span className="text-sm font-medium text-gray-900">
                  {MODULE_LABELS[key] || key}
                </span>
                <span className="ml-2 text-xs text-gray-400">{href}</span>
              </div>
              {isEnabled && (
                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                  Active
                </span>
              )}
            </label>
          )
        })}
      </div>
    </div>
  )
}
